import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { programs } from '@/mocks/neurobaby';

const routeMap: Record<number, string> = {
  1: '/fundamentos-sueno',
  2: '/yoga-sueno-profundo',
  3: '/programa-completo-neurobabys',
};

const chooseItems = [
  { id: 1, icon: 'ri-star-line' },
  { id: 2, icon: 'ri-heart-2-line' },
  { id: 3, icon: 'ri-medal-line' },
];

export default function Programas() {
  const { t } = useTranslation();
  const [hoveredProgram, setHoveredProgram] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero Section — Imagen a pleno con texto encima */}
      <section className="relative min-h-[540px] md:min-h-[680px] flex items-center overflow-hidden">
        {/* Imagen de fondo a pleno en toda la página */}
        <img
          src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/9020c9cd-aee8-4ddf-bbb7-80780c621b0c_compressed_ChatGPT-Image-13-ago-2026-04_06_44-p.m.-1.webp"
          alt={t('programas.hero.title1')}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* Overlay oscuro para que el texto se lea bien */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10"></div>

        {/* Contenido — texto a la izquierda, sin tocar la imagen de Mercedes */}
        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="max-w-xl">
            <span className="inline-block font-sans text-xs md:text-sm font-semibold text-white uppercase tracking-[0.2em] mb-5 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              {t('programas.hero.badge')}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3">
              {t('programas.hero.title1')}
            </h1>
            <p className="font-serif text-lg md:text-xl text-white/90 italic mb-6">
              {t('programas.hero.title2')}
            </p>
            <p className="font-sans text-sm md:text-base text-white/85 leading-relaxed max-w-lg mb-8">
              {t('programas.hero.desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link
                to="/conoce-el-metodo"
                className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                {t('programas.hero.cta_metodo')}
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/quiero-cambiar"
                className="inline-flex items-center justify-center bg-white text-[#3D2B1F] px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-cream transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
              >
                {t('programas.hero.cta_gratis')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-20 px-6 md:px-10 bg-white">
        <div className="w-full max-w-3xl mx-auto text-center">
          <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
            {t('programas.intro.tag')}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
            {t('programas.intro.title')}
          </h2>
          <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
            {t('programas.intro.desc')}
          </p>
        </div>
      </section>

      {/* Program Cards Section */}
      <section className="pb-16 md:pb-24 px-6 md:px-10 bg-white" data-product-shop>
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {programs.map((program) => (
              <div
                key={program.id}
                className="bg-cream rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:shadow-lg hover:border-terracotta/30 flex flex-col"
                onMouseEnter={() => setHoveredProgram(program.id)}
                onMouseLeave={() => setHoveredProgram(null)}
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={program.image}
                    alt={t(`programs.items.${program.id}.title`, program.title)}
                    className="w-full h-full object-cover transition-transform duration-500"
                    style={{
                      transform: hoveredProgram === program.id ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="font-sans text-xs font-semibold text-[#8B6F5C]">
                      {t(`programs.items.${program.id}.level`, program.level)}
                    </span>
                  </div>
                  {'badge' in program && (program as any).badge && (
                    <div className="absolute top-3 right-3 bg-terracotta text-white px-3 py-1 rounded-full">
                      <span className="font-sans text-xs font-semibold">{(program as any).badge}</span>
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-1">
                    {t(`programs.items.${program.id}.subtitle`, program.subtitle)}
                  </span>
                  <h3 className="font-serif text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                    {t(`programs.items.${program.id}.title`, program.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-4 flex-grow">
                    {t(`programs.items.${program.id}.description`, program.description)}
                  </p>
                  <div className="flex items-center gap-3 mb-4 text-sm text-[#5C4A3A]">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-xs" />
                      {program.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line text-xs" />
                      {program.lessons} {t('common.guias')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-sand/30">
                    <div>
                      <span className="font-sans text-2xl font-bold text-[#8B6F5C]">${program.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <Link
                      to={routeMap[program.id]}
                      className="inline-flex items-center bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                    >
                      {t('common.conocer_mas')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Which Program Is for You */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-sand">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('programas.elegir.tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('programas.elegir.title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('programas.elegir.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {chooseItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-6 md:p-8 border border-sand/40 flex flex-col items-start"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-terracotta/15 text-terracotta mb-5">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-serif text-xl font-bold text-[#8B6F5C] mb-2 leading-tight">
                  {t(`programas.elegir.${item.id}.title`)}
                </h3>
                <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                  {t(`programas.elegir.${item.id}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-4xl mx-auto rounded-3xl bg-dark-brown px-6 md:px-16 py-12 md:py-16 text-center">
          <span className="inline-block font-sans text-xs font-semibold text-cream uppercase tracking-[0.2em] mb-4">
            {t('programas.cta.tag')}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-cream leading-tight mb-4">
            {t('programas.cta.title')}
          </h2>
          <p className="font-sans text-base text-cream leading-relaxed max-w-xl mx-auto mb-8">
            {t('programas.cta.desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/quiero-cambiar"
              className="inline-flex items-center justify-center bg-white text-[#3D2B1F] px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-cream transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
            >
              {t('programas.cta.button1')}
            </Link>
            <Link
              to="/conoce-el-metodo"
              className="inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
            >
              {t('programas.cta.button2')}
              <i className="ri-arrow-right-line ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}